import { TAO_KOE, DynamicYieldAPI } from './../libs';
import renderSearchSuggestionsProduct from './templates/search-suggestions-product.tpl.html';
import renderSearchSuggestionsSearchterm from './templates/search-suggestions-searchterm.tpl.html';
import renderSearchSuggestionsSkeleton from './templates/search-suggestions-skeleton.tpl.html';

class DynamicYieldSearchSuggestions {
	isOpen = false;
	renderedInitialProducts = false;
	requestId = 0;
	renderedLiveSuggestionsQuery = '';
	pendingLiveSuggestionsQuery = '';

	constructor( searchEl ) {
		this.searchEl = searchEl;
		this.formEl = searchEl.querySelector( '.mdl_search__form' );
		this.inputEl = searchEl.querySelector( '.mdl_search__input' );
		this.dialogEl = searchEl.querySelector( '.mdl_search__box' );
		this.initialProductsEl = searchEl.querySelector( '.mdl_search__initial .mdl_search__suggestions-products' );
		this.initialProductsLoadingEl = this.initialProductsEl?.closest( '.mdl_search__container' );
		this.suggestionsEl = searchEl.querySelector( '.mdl_search__suggestions' );
		this.liveSearchTermsContainerEl = this.suggestionsEl?.querySelector( '.mdl_search__container--live-searchterms' );
		this.liveSearchTermsEl = this.liveSearchTermsContainerEl?.querySelector( '.mdl_search__suggestions-searchterms' );
		this.liveProductsContainerEl = this.suggestionsEl?.querySelector( '.mdl_search__container--live-products' );
		this.liveProductsEl = this.liveProductsContainerEl?.querySelector( '.mdl_search__suggestions-products' );

		if ( ! this.inputEl || ! this.suggestionsEl || ! this.liveSearchTermsEl || ! this.liveProductsEl ) {
			return;
		}

		this.bindEvents();
	}

	bindEvents() {
		this.inputEl.addEventListener( 'input', () => this.onInput() );
		this.inputEl.addEventListener( 'focusin', () => this.onFocus() );
		this.formEl?.addEventListener( 'submit', ( ev ) => this.onSubmit( ev ) );

		this.searchEl.addEventListener( 'click', ( ev ) => {
			const searchTermEl = ev.target.closest( '.mdl_search__suggestions-searchterm' );
			if ( searchTermEl ) {
				this.updateSearchTermLink( searchTermEl );
			}

			if ( ev.target.matches( 'input[type=search]' ) ) {
				this.open();
			} else if ( ev.target.closest( '.mdl_search__close-button,.mdl_search__box-close-button' ) ) {
				this.close();
			}
		} );

		document.addEventListener( 'click', ( ev ) => {
			if ( ev.target.closest( '.mdl_header__user-nav-item--search .mdl_header__user-nav-item-link' ) ) {
				this.open();
			} else {
				this.maybeClose( ev );
			}
		} );
		document.addEventListener( 'focusin', ( ev ) => this.maybeClose( ev ) );
		document.addEventListener( 'keydown', ( ev ) => this.maybeClose( ev ) );

		if ( this.initialProductsEl ) {
			[ 'click', 'touchstart', 'mouseenter', 'focusin', 'open_searchbox.sl' ].forEach( ( eventName ) => {
				this.searchEl.addEventListener( eventName, () => this.renderInitialProducts(), { once: true, passive: true } );
			} );
		}
	}

	updateSearchTermLink( linkEl ) {
		if ( ! linkEl.href ) {
			return;
		}

		const searchParams = new URLSearchParams( linkEl.search );
		const userInput = this.inputEl.value.trim();

		if ( userInput ) {
			searchParams.set( 'userInput', userInput );
		} else {
			searchParams.delete( 'userInput' );
		}

		linkEl.search = searchParams.toString();
	}

	open() {
		this.isOpen = true;
		this.searchEl.classList.add( 'is-active' );
		this.dialogEl?.dispatchEvent( new CustomEvent( 'open_searchbox.sl', { bubbles: true } ) );
	}

	close() {
		this.isOpen = false;
		this.searchEl.classList.remove( 'is-active' );
		this.dialogEl?.dispatchEvent( new CustomEvent( 'close_searchbox.sl', { bubbles: true } ) );
	}

	maybeClose( ev ) {
		if ( ! this.isOpen ) {
			return;
		}

		if ( ev.type === 'keydown' ) {
			if ( ev.key === 'Escape' || ev.keyCode === 27 ) {
				this.close();
			}
			return;
		}

		if ( ! ev.target.closest( 'search.mdl_search' ) ) {
			this.close();
		}
	}

	onFocus() {
		this.open();

		const query = this.inputEl.value.trim();
		if ( this.isHoneypotFilled() || query.length < 3 ) {
			return;
		}

		this.renderLiveSuggestionsForQuery( query, { immediate: true } );
	}

	onInput() {
		const query = this.inputEl.value.trim();

		if ( this.isHoneypotFilled() || query.length < 3 ) {
			this.resetLiveSuggestions();
			return;
		}

		this.renderLiveSuggestionsForQuery( query );
	}

	onSubmit( ev ) {
		if ( this.isHoneypotFilled() ) {
			ev.preventDefault();
			return;
		}

		this.trackSearch();
	}

	isHoneypotFilled() {
		const fakeInput = this.formEl.querySelector( '.mdl_search__fakeinput' );

		return fakeInput && fakeInput.value.trim() !== '';
	}

	resetLiveSuggestions() {
		clearTimeout( this.timeout );
		this.requestId++;
		this.renderedLiveSuggestionsQuery = '';
		this.pendingLiveSuggestionsQuery = '';
		this.clearLiveSuggestions();
		this.searchEl.classList.remove( 'has-results', 'has-noresults', 'has-error' );
	}

	renderLiveSuggestionsForQuery( query, { immediate = false, loading = true } = {} ) {
		const normalizedQuery = this.normalizeLiveSuggestionsQuery( query );

		if ( this.hasRenderedLiveSuggestionsForQuery( normalizedQuery ) || this.pendingLiveSuggestionsQuery === normalizedQuery ) {
			return;
		}

		clearTimeout( this.timeout );
		this.requestId++;
		this.renderedLiveSuggestionsQuery = normalizedQuery;
		this.pendingLiveSuggestionsQuery = '';
		this.searchEl.classList.remove( 'has-noresults', 'has-error' );
		this.searchEl.classList.add( 'has-results' );

		if ( loading || immediate ) {
			this.renderLoadingState();
		}

		const requestId = this.requestId;
		const render = () => {
			this.pendingLiveSuggestionsQuery = normalizedQuery;
			this.renderLiveSuggestions( query, requestId );
		};

		if ( immediate ) {
			render();
			return;
		}

		this.timeout = setTimeout( () => {
			render();
		}, 250 );
	}

	trackSearch() {
		const term = this.inputEl.value.trim();

		if ( ! term ) {
			return;
		}

		document.dispatchEvent( new CustomEvent( 'perform_search.sl', {
			bubbles: true,
			detail: {
				term,
			},
		} ) );
	}

	async renderInitialProducts() {
		if ( this.renderedInitialProducts || ! this.initialProductsEl ) {
			return;
		}

		this.renderedInitialProducts = true;

		try {
			const suggestions = await this.getInitialProducts();
			this.initialProductsEl.replaceChildren( this.renderProducts( suggestions ) );
			this.initialProductsLoadingEl?.classList.remove( 'is-loading' );
		} catch ( err ) {
			this.initialProductsLoadingEl?.classList.remove( 'is-loading' );
			// eslint-disable-next-line no-console
			console.error( 'Dynamic Yield initial search suggestions failed:', err );
		}
	}

	async getInitialProducts() {
		const selector = this.initialProductsEl.dataset.selector || 'API_Widget_CMS_Search_Initial_Suggestions';
		const choice = await DynamicYieldAPI.getChoices( selector, {
			'context': window.DY?.recommendationContext,
			'options': {
				'recsProductData': {
					'fieldFilter': [
						'name',
						'brand',
						'url',
						'image_url',
					],
				},
			},
		} ).then( ( { choices } ) => choices[ selector ] );

		const slots = choice?.variations?.[ 0 ]?.payload?.data?.slots || [];
		return this.mapProductSlots( slots );
	}

	async renderLiveSuggestions( query, requestId ) {
		const state = {
			query,
			normalizedQuery: this.normalizeLiveSuggestionsQuery( query ),
			suggest: [],
			products: [],
			suggestDone: false,
			productsDone: false,
			suggestFailed: false,
			productsFailed: false,
		};

		await Promise.allSettled( [
			this.renderLiveSuggestionsRequest( {
				requestId,
				state,
				promise: DynamicYieldAPI.suggest( query ),
				doneKey: 'suggestDone',
				failedKey: 'suggestFailed',
				errorMessage: 'Dynamic Yield live search term suggestions failed:',
				onSuccess: ( data ) => {
					state.suggest = this.mapSuggestData( data );
					this.renderLiveSearchTerms( query, state.suggest );
				},
				onError: () => {
					this.renderLiveSearchTerms( query, [] );
				},
			} ),
			this.renderLiveSuggestionsRequest( {
				requestId,
				state,
				promise: DynamicYieldAPI.searchSuggestProducts( query ),
				doneKey: 'productsDone',
				failedKey: 'productsFailed',
				errorMessage: 'Dynamic Yield live search product suggestions failed:',
				onSuccess: ( data ) => {
					state.products = this.mapSearchProductsData( data );
					this.renderLiveProducts( state );
				},
				onError: () => {
					this.renderLiveProducts( state );
				},
			} ),
		] );

		if ( this.pendingLiveSuggestionsQuery === state.normalizedQuery ) {
			this.pendingLiveSuggestionsQuery = '';
		}
	}

	async renderLiveSuggestionsRequest( { requestId, state, promise, doneKey, failedKey, errorMessage, onSuccess, onError } ) {
		try {
			const data = await promise;
			if ( requestId !== this.requestId ) {
				return;
			}

			onSuccess( data );
		} catch ( err ) {
			if ( requestId !== this.requestId ) {
				return;
			}

			state[ failedKey ] = true;
			onError();
			// eslint-disable-next-line no-console
			console.error( errorMessage, err );
		} finally {
			if ( requestId !== this.requestId ) {
				return;
			}

			state[ doneKey ] = true;
			this.updateLiveSuggestionsState( state );
		}
	}

	renderLiveSearchTerms( query, suggest ) {
		this.liveSearchTermsEl.replaceChildren();
		this.liveSearchTermsContainerEl.classList.remove( 'is-loading' );
		this.liveSearchTermsContainerEl.classList.toggle( 'en_hidden', ! suggest.length );

		if ( suggest.length ) {
			this.liveSearchTermsEl.replaceChildren( this.renderSearchTerms( query, suggest ) );
		}
	}

	renderLiveProducts( state ) {
		const { suggest, suggestDone, products } = state;

		this.liveProductsEl.replaceChildren();
		this.liveProductsContainerEl.classList.remove( 'is-loading' );
		this.liveProductsContainerEl.classList.toggle( 'en_hidden', ! products.length );
		this.liveProductsContainerEl.classList.toggle( 'mdl_search__container--products-only', products.length && suggestDone && ! suggest.length );

		if ( products.length ) {
			this.liveProductsEl.replaceChildren( this.renderProducts( products, { showAll: true, userInput: state.query } ) );
		}
	}

	updateLiveSuggestionsState( state ) {
		if ( state.products.length ) {
			this.liveProductsContainerEl.classList.toggle( 'mdl_search__container--products-only', state.suggestDone && ! state.suggest.length );
		}

		if ( ! state.suggestDone || ! state.productsDone ) {
			return;
		}

		this.searchEl.classList.remove( 'has-results', 'has-noresults', 'has-error' );

		if ( state.suggestFailed && state.productsFailed ) {
			this.clearLiveSuggestions();
			this.searchEl.classList.add( 'has-error' );
			return;
		}

		this.searchEl.classList.add( state.suggest.length || state.products.length ? 'has-results' : 'has-noresults' );
	}

	renderProducts( suggestions, { showAll = false, userInput = '' } = {} ) {
		const html = renderSearchSuggestionsProduct( {
			suggestions,
			showAll,
			userInput,
			mark: ( input ) => input,
		} );

		return document.createRange().createContextualFragment( html );
	}

	renderSearchTerms( query, suggest ) {
		const html = renderSearchSuggestionsSearchterm( {
			query,
			suggestions: suggest,
		} );

		return document.createRange().createContextualFragment( html );
	}

	renderLoadingState() {
		this.clearLiveSuggestions();
		this.liveSearchTermsContainerEl.classList.remove( 'en_hidden' );
		this.liveSearchTermsContainerEl.classList.add( 'is-loading' );
		this.liveProductsContainerEl.classList.remove( 'en_hidden' );
		this.liveProductsContainerEl.classList.add( 'is-loading' );

		const searchTermsHtml = renderSearchSuggestionsSkeleton( { type: 'searchterms', count: 8 } );
		const productsHtml = renderSearchSuggestionsSkeleton( { type: 'products', count: 12 } );

		this.liveSearchTermsEl.replaceChildren( document.createRange().createContextualFragment( searchTermsHtml ) );
		this.liveProductsEl.replaceChildren( document.createRange().createContextualFragment( productsHtml ) );
	}

	clearLiveSuggestions() {
		this.liveSearchTermsEl.replaceChildren();
		this.liveProductsEl.replaceChildren();
		this.liveSearchTermsContainerEl.classList.remove( 'is-loading' );
		this.liveProductsContainerEl.classList.remove( 'is-loading' );
		this.liveProductsContainerEl.classList.remove( 'mdl_search__container--products-only' );
		this.liveSearchTermsContainerEl.classList.add( 'en_hidden' );
		this.liveProductsContainerEl.classList.add( 'en_hidden' );
	}

	normalizeLiveSuggestionsQuery( query ) {
		return query.trim().toLowerCase();
	}

	hasRenderedLiveSuggestionsForQuery( normalizedQuery ) {
		return this.renderedLiveSuggestionsQuery === normalizedQuery &&
			! this.liveSearchTermsContainerEl.classList.contains( 'is-loading' ) &&
			! this.liveProductsContainerEl.classList.contains( 'is-loading' ) &&
			(
				this.searchEl.classList.contains( 'has-results' ) ||
				this.searchEl.classList.contains( 'has-noresults' ) ||
				this.searchEl.classList.contains( 'has-error' )
			);
	}

	mapSuggestData( data ) {
		const suggestData = data?.suggest || data;
		const querySuggestions = suggestData?.variations?.[ 0 ]?.payload?.data?.suggestions?.querySuggestions || [];
		const suggestions = querySuggestions.map( ( suggestion ) => ( {
			name: suggestion.term,
		} ) ).filter( ( suggestion ) => suggestion.name );

		return suggestions;
	}

	mapSearchProductsData( data ) {
		const searchData = data?.search || data;
		const variation = searchData?.variations?.[ 0 ] || searchData?.choices?.[ 0 ]?.variations?.[ 0 ] || {};
		const slots = variation?.payload?.data?.slots || [];

		return this.mapProductSlots( slots );
	}

	mapProductSlots( slots ) {
		return slots.map( ( slot ) => {
			const productData = slot.productData || slot.product || slot.item || slot;
			const imageUrl = productData.image_url || productData.imageUrl || productData.image || productData.ImageURL || '';
			const link = productData.relative_url || productData.url || productData.productUrl || productData.ProductURL || '#';

			return {
				sku: slot.sku || productData.sku,
				slotId: slot.slotId,
				index: slot.index,
				brand: productData.brand || productData.Brand || '',
				name: productData.name || productData.title || productData.sku || slot.sku || '',
				ImageURL: imageUrl ? imageUrl.replace( '768_1024_75', '192_256_75' ) : '',
				Deeplink: link,
			};
		} ).filter( ( product ) => product.name && product.sku );
	}
}

TAO_KOE.ready( 'dynamic-yield-search-suggestions.js', () => {
	document.querySelectorAll( 'search.mdl_search' ).forEach( ( searchEl ) => {
		searchEl.KOE_DYNAMIC_YIELD_SEARCH_SUGGESTIONS ??= new DynamicYieldSearchSuggestions( searchEl );
	} );
} );

export default DynamicYieldSearchSuggestions;
